import { Box, Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import type { ISuggestion } from '@sharedInterfaces/IEmployeeSuggestions';
import type { ISuggestionData } from '@sharedInterfaces/IEmployeeTalkState';
import type { ISuggestionComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import SuggestionForm from '@src/App/Components/Dialogs/SuggestionsDialog/SuggestionForm';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';

import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';

export interface ETTSuggestionComponentProps extends ITemplateComponentProps {
    value?: ISuggestionData;
}

export const ETTSuggestionComponent: React.FC<ETTSuggestionComponentProps> = (props) => {
    const { component, onChange, value } = props;
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).suggestions;
    const ettLangStrings = getLanguageTexts(lang).employeeTalk;
    const oftenUsed = ettLangStrings.oftenUsed;
    const currentUser = useSelector((state: AppState) => state.employee);

    // Extrahiere die Eigenschaften aus den Komponenteneigenschaften oder setze Standardwerte
    const properties = component.properties as ISuggestionComponentProperties;
    const enabledEntities = properties?.enabledEntities || { skills: true, certificates: true, roles: true };
    const requireJustification = properties?.requireJustification || false;

    // Verwende den übergebenen State oder den gespeicherten State aus den Properties
    const defaultState: ISuggestionData = {
        acceptedSuggestions: [],
        rejectedSuggestions: [],
        skills: [],
        certificates: [],
        roles: [],
        justification: '',
        lastModified: new Date().toISOString(),
    };

    const formState: ISuggestionData =
        value ||
        (properties?.formState
            ? {
                  acceptedSuggestions: [],
                  rejectedSuggestions: [],
                  ...properties.formState,
                  lastModified: (properties.formState as any).lastModified || new Date().toISOString(),
              }
            : defaultState);

    // Aktualisiere die Komponenteneigenschaften
    const updateProperties = (updates: Partial<ISuggestionComponentProperties>) => {
        if (onChange) {
            onChange({
                ...component,
                properties: {
                    ...properties,
                    ...updates,
                },
            });
        }
    };

    // Handler für Änderungen an den aktivierten Entitäten
    const handleEntityToggle =
        (entity: 'skills' | 'certificates' | 'roles') => (event: React.ChangeEvent<HTMLInputElement>) => {
            updateProperties({
                enabledEntities: {
                    ...enabledEntities,
                    [entity]: event.target.checked,
                },
            });
        };

    // Handler für Änderungen an der Begründungspflicht
    const handleRequireJustificationToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        updateProperties({
            requireJustification: event.target.checked,
        });
    };

    // Handler für Änderungen am Formular-State
    const handleSkillsChange = (skills: { title: string; id?: number; level: number }[]) => {
        if (onChange) {
            onChange({
                skills,
                certificates: formState.certificates,
                roles: formState.roles,
                justification: formState.justification,
                lastModified: new Date().toISOString(),
            });
        }
    };

    const handleCertificatesChange = (certificates: { title: string; id?: number }[]) => {
        if (onChange) {
            onChange({
                skills: formState.skills,
                certificates,
                roles: formState.roles,
                justification: formState.justification,
                lastModified: new Date().toISOString(),
            });
        }
    };

    const handleRolesChange = (roles: { title: string; id: number }[]) => {
        if (onChange) {
            onChange({
                skills: formState.skills,
                certificates: formState.certificates,
                roles,
                justification: formState.justification,
                lastModified: new Date().toISOString(),
            });
        }
    };

    const handleJustificationChange = (justification: string) => {
        if (onChange) {
            onChange({
                skills: formState.skills,
                certificates: formState.certificates,
                roles: formState.roles,
                justification,
                lastModified: new Date().toISOString(),
            });
        }
    };

    // Erstelle eine Suggestion aus den aktuellen Daten
    const createSuggestion = (): ISuggestion => {
        return {
            suggestorId: currentUser.id,
            employeeId: -1, // Wird später festgelegt
            elements: {
                skills: formState.skills.map((s) => ({ id: s.id, title: s.title, level: s.level })),
                certificates: formState.certificates,
                roles: formState.roles,
            },
            justification: formState.justification,
            status: 'OPEN',
        };
    };

    // Render-Funktion für den Edit-Modus
    const renderEditContent = () => {
        return (
            <Box>
                <RowElement title={oftenUsed.settings}>
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox checked={enabledEntities.skills} onChange={handleEntityToggle('skills')} />
                            }
                            label={langStrings.oftenUsed.skills}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={enabledEntities.certificates}
                                    onChange={handleEntityToggle('certificates')}
                                />
                            }
                            label={langStrings.oftenUsed.certificates}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox checked={enabledEntities.roles} onChange={handleEntityToggle('roles')} />
                            }
                            label={langStrings.oftenUsed.roles}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox checked={requireJustification} onChange={handleRequireJustificationToggle} />
                            }
                            label={langStrings.requireJustification || 'Require justification'}
                        />
                    </FormGroup>
                </RowElement>
            </Box>
        );
    };

    // Render-Funktion für den Anzeige-Modus
    const renderViewContent = () => {
        // Erstelle eine Suggestion aus dem gespeicherten State für die Anzeige
        const suggestionFromState: ISuggestion = {
            suggestorId: currentUser.id,
            employeeId: -1,
            elements: {
                skills: formState.skills,
                certificates: formState.certificates,
                roles: formState.roles,
            },
            justification: formState.justification,
            status: 'OPEN',
        };

        return (
            <Box>
                <SuggestionForm
                    suggestion={suggestionFromState}
                    showEmployeeSelect={false}
                    requireJustification={requireJustification}
                    enabledEntities={enabledEntities}
                    onSkillsChange={handleSkillsChange}
                    onCertificatesChange={handleCertificatesChange}
                    onRolesChange={handleRolesChange}
                    onJustificationChange={handleJustificationChange}
                />
            </Box>
        );
    };

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={renderEditContent}
            renderViewContent={renderViewContent}
        />
    );
};

export default ETTSuggestionComponent;
